import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Box,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Plus,
  Trash2,
} from "lucide-react";
import { DataGrid, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton } from "@mui/x-data-grid";
import { EntitiesListResponseModelShape } from "../models/EntitiesListResponseModel";
import { tokens } from "../../theme";

const EditToolbar = (props) => {
  const {
    addRecordButtonTitle,
    canBeDeleted,
    lunchCreateModal,
    lunchDeletionModal,
    selectedRows,
    tableTitle,
  } = props;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <GridToolbarContainer
      sx={{
        p: "20px 24px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: `1px solid ${theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[200]}`,
        bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)"
      }}
    >
      <Box>
        <Typography
          variant="h4"
          sx={{
            color: colors.grey[100],
            fontWeight: "700",
            fontFamily: "Josefin Sans",
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {tableTitle}
          <Typography variant="caption" sx={{ color: colors.greenAccent[500], bgcolor: `${colors.greenAccent[500]}1A`, px: 1, py: 0.5, borderRadius: '4px' }}>
            List Mode
          </Typography>
        </Typography>
      </Box>

      <Box display="flex" gap="12px" alignItems="center">
        <GridToolbarColumnsButton sx={{ color: colors.grey[300], textTransform: 'none', fontWeight: '600' }} />
        <GridToolbarFilterButton sx={{ color: colors.grey[300], textTransform: 'none', fontWeight: '600' }} />

        {selectedRows?.length > 0 && canBeDeleted && (
          <Button
            variant="contained"
            startIcon={<Trash2 size={18} />}
            onClick={() => lunchDeletionModal(selectedRows)}
            sx={{
              backgroundColor: colors.redAccent[500],
              color: "#FFFFFF",
              fontWeight: "600",
              textTransform: "none",
              borderRadius: "8px",
              px: 2,
              "&:hover": {
                backgroundColor: colors.redAccent[600],
                transform: "translateY(-1px)"
              },
              transition: "all 0.2s"
            }}
          >
            Delete ({selectedRows.length})
          </Button>
        )}

        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={lunchCreateModal}
          sx={{
            backgroundColor: colors.greenAccent[500],
            color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
            fontWeight: "700",
            textTransform: "none",
            borderRadius: "8px",
            px: 3,
            "&:hover": {
              backgroundColor: colors.greenAccent[400],
              transform: "translateY(-1px)",
              boxShadow: `0 4px 12px ${colors.greenAccent[500]}33`
            },
            transition: "all 0.2s"
          }}
        >
          {addRecordButtonTitle}
        </Button>
      </Box>
    </GridToolbarContainer>
  );
};

const CustomDataTable = (props) => {
  const {
    addRecordButtonTitle,
    canBeDeleted = true,
    headerColumn,
    lunchCreateModal,
    lunchDeletionModal,
    paginationModel,
    rows,
    setPaginationModel,
    tableTitle,
  } = props;

  const [selectedRows, setSelectedRows] = useState([]);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box
      sx={{
        width: "100%",
        "& .MuiDataGrid-root": {
          border: "none",
          borderRadius: "20px",
          backgroundColor: theme.palette.mode === "dark" ? colors.primary[400] : "#FFFFFF",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
          overflow: 'hidden'
        },
        "& .MuiDataGrid-cell": {
          borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
          py: 2,
          display: 'flex',
          alignItems: 'center'
        },
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
          borderBottom: `1px solid ${theme.palette.mode === "dark" ? colors.primary[300] : colors.grey[200]}`,
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "700",
            fontFamily: "Josefin Sans",
            color: colors.grey[100],
            fontSize: '0.9rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }
        },
        "& .MuiDataGrid-virtualScroller": {
          backgroundColor: "transparent",
        },
        "& .MuiDataGrid-footerContainer": {
          borderTop: `1px solid ${theme.palette.mode === "dark" ? colors.primary[300] : colors.grey[200]}`,
          backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
          p: 1
        },
        "& .MuiDataGrid-row": {
          transition: "all 0.2s",
          "&:hover": {
            backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.04) !important" : "rgba(0,0,0,0.02) !important",
            cursor: 'pointer'
          }
        },
        "& .MuiCheckbox-root": {
          color: `${colors.greenAccent[500]} !important`,
        },
        "& .MuiTablePagination-root": {
          color: colors.grey[100],
          fontFamily: "Josefin Sans",
          fontWeight: "600"
        }
      }}
    >
      <DataGrid
        rows={rows.content}
        columns={headerColumn}
        autoHeight
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
        }}
        pageSizeOptions={[5, 10, 12, 20, 50]}
        rowCount={rows.page?.totalElements || rows.totalElements || rows.content?.length || 0}
        paginationModel={paginationModel}
        paginationMode="server"
        onPaginationModelChange={setPaginationModel}
        checkboxSelection
        disableRowSelectionOnClick
        keepNonExistentRowsSelected
        onRowSelectionModelChange={(newSelectionModel) => {
          setSelectedRows(newSelectionModel);
        }}
        slots={{
          toolbar: () => (
            <EditToolbar
              addRecordButtonTitle={addRecordButtonTitle}
              canBeDeleted={canBeDeleted}
              lunchCreateModal={lunchCreateModal}
              lunchDeletionModal={lunchDeletionModal}
              selectedRows={selectedRows}
              tableTitle={tableTitle}
            />
          ),
        }}
      />
    </Box>
  );
};

CustomDataTable.propTypes = {
  addRecordButtonTitle: PropTypes.string.isRequired,
  canBeDeleted: PropTypes.bool,
  headerColumn: PropTypes.array.isRequired,
  lunchCreateModal: PropTypes.func.isRequired,
  lunchDeletionModal: PropTypes.func,
  paginationModel: PropTypes.object.isRequired,
  rows: EntitiesListResponseModelShape.isRequired,
  setPaginationModel: PropTypes.func.isRequired,
  tableTitle: PropTypes.string.isRequired,
};

export default CustomDataTable;
